/**
 * pending-auth-email — carries the addressee between /auth/signup|forgot and
 * /auth/check-email WITHOUT putting it in the URL.
 *
 * WHY (2026-08-25). Paul's prod walkthrough was recorded, and the address bar
 * read:
 *
 *   elreign.io/auth/check-email?email=paulprinsloo078%2B6%40gmail.com&flow=signup
 *
 * An email address is personal information under POPIA. In a query string it
 * lands in browser history, in the `Referer` header of every subsequent
 * navigation, in any proxy or CDN access log on the path, and — as the
 * recording proves — in screen shares. None of that is necessary: only this
 * tab, for the next few minutes, needs to know which inbox to name.
 *
 * sessionStorage is the right carrier. It never leaves the browser, it is
 * same-origin scoped, it survives the refresh that router state would lose,
 * and it dies with the tab.
 *
 * `flow` stays in the URL — it is not personal information and the page is
 * meaningfully different for signup vs forgot.
 */

const KEY = 'elreign.pendingAuthEmail';

/** sessionStorage throws in private modes and is absent in SSR/test DOMs. */
function store(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Remember the addressee for the check-email screen in this tab only. */
export function stashPendingAuthEmail(email: string): void {
  const s = store();
  if (!s) return;
  try {
    s.setItem(KEY, email);
  } catch {
    /* quota or private mode — the screen falls back to the session email */
  }
}

export function readPendingAuthEmail(): string | null {
  const s = store();
  if (!s) return null;
  try {
    const v = s.getItem(KEY);
    return v && v.trim() !== '' ? v : null;
  } catch {
    return null;
  }
}

export function clearPendingAuthEmail(): void {
  const s = store();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Strip `?email=` from the address bar without a navigation.
 *
 * Covers links already in flight from the old build, and anyone who bookmarked
 * or was sent one: the address is adopted into sessionStorage and then removed
 * from the URL, so it stops accumulating in history from that point on.
 *
 * Uses replaceState so it does not add a history entry of its own.
 */
export function scrubEmailFromUrl(): void {
  try {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('email')) return;
    url.searchParams.delete('email');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  } catch {
    /* a failed scrub must never break the page */
  }
}
